import * as React from "react";
import "./TreeView.scss";
import {
    RichTreeViewPro as MaterialTreeView,
    TreeItemProps as MaterialTreeViewItemProps,
    RichTreeViewProProps as MaterialTreeViewProps,
} from "@mui/x-tree-view-pro";
import { useTreeItem } from '@mui/x-tree-view/useTreeItem';
import { useTreeItemUtils } from '@mui/x-tree-view/hooks';
import {
    TreeItemContent,
    TreeItemIconContainer,
    TreeItemGroupTransition,
    TreeItemLabel,
    TreeItemRoot,
    TreeItemCheckbox,
} from '@mui/x-tree-view/TreeItem';
import { TreeItemLabelInput } from '@mui/x-tree-view/TreeItemLabelInput';
import { TreeItemIcon } from '@mui/x-tree-view/TreeItemIcon';
import { TreeItemProvider } from '@mui/x-tree-view/TreeItemProvider';
import { TreeItemDragAndDropOverlay } from '@mui/x-tree-view/TreeItemDragAndDropOverlay';
import IconButton from '@mui/material/IconButton';
import { OutlinedInput, InputAdornment } from ".";
import { TriangleRight, TriangleDown, Search, DragVertical, Pencil, Check, X } from "./icons";
import * as _ from "lodash";
import { BehaviorSubject } from "rxjs";
import { tap, debounceTime } from 'rxjs/operators';
import clsx from "clsx";
import { Tooltip } from "../../viewer/components/Tooltip";
import { structuralEqualityExcludingTransientEntries } from "../utilities";
import { SearchTermHighlight } from "./SearchTermHighlight";
import { getDataProps, removeDataProps } from "../utilities";
import moment from "moment";

export type TreeViewNode = {
    id?: string;
    depth?: number;
    value?: any;
    label: string;
    children?: TreeViewNode[];
    preventSelection?: boolean;
    isExpanded?: boolean;
    isSelected?: boolean;
    isEditable?: boolean;
    additionalSearchTerms?: string[];
    [key: string]: any;
};

export type TreeViewDataSource = {
    getChildrenCount: (item: TreeViewNode) => number;
    getTreeItems: (parentId?: string) => Promise<TreeViewNode[]>;
};

type BaseTreeViewProps<T, Multiple extends boolean | undefined> = Omit<MaterialTreeViewProps<T, Multiple>, "items" | "dataSource"> & {
    name?: string;
    nodes?: TreeViewNode[];
    dataSource?: TreeViewDataSource;
    multiSelect?: boolean;
    multiSelectWithoutCtrl?: boolean;
    showSearch?: boolean;
    defaultSearchTerm?: string;
    onSelect?: (selectedNodes: TreeViewNode[]) => void;
    onLabelChange?: (node: TreeViewNode, newLabel: string) => boolean | void;
    onNodeReorder?: (node: TreeViewNode, oldPosition: TreeItemPosition, newPosition: TreeItemPosition) => boolean | void;
    getNodeDropAllowed?: (node: TreeViewNode, oldPosition: TreeItemPosition, newPosition: TreeItemPosition) => boolean;
    getNodeTooltip?: (node: TreeViewNode) => any;
    getItemId?: (node: TreeViewNode) => string;
    getNodeSelectionDisabled?: (node: TreeViewNode) => boolean;
    renderNode?: (item: TreeViewNode, depth: number, searchTerm?: string) => React.ReactNode;
    maxSelectionCount?: number;
    editNodeIcon?: React.ReactNode;
    acceptEditNodeIcon?: React.ReactNode;
    cancelEditNodeIcon?: React.ReactNode;
    dragIcon?: React.ReactNode;
    defaultExpandIcon?: React.ReactNode;
    defaultCollapseIcon?: React.ReactNode;
};

type SingleSelectTreeViewProps<T> = BaseTreeViewProps<T, boolean> & {
    multiSelect?: false;
};

type MultiSelectTreeViewProps<T> = BaseTreeViewProps<T, boolean> & {
    multiSelect?: true;
};

export type TreeViewProps<T> = SingleSelectTreeViewProps<T> | MultiSelectTreeViewProps<T>;

export type TreeItemPosition = {
    index: number;
    parentId: string | null;
}

type OnNodeReorderParams = {
    itemId: string;
    oldPosition: TreeItemPosition;
    newPosition: TreeItemPosition;
}


function getFullHierarchy(node: TreeViewNode): TreeViewNode[] {
    const allChildren = _.flatMap(node.children, getFullHierarchy);
    return [node, ...allChildren];
}

function hasAncestorWithClassName(element: HTMLElement, className: string) {
    if (!element) return false;
    if (element.classList.contains(className)) return true;

    return hasAncestorWithClassName(element.parentElement, className);
}


function nodeMatchesSearchTerm(node: TreeViewNode, searchTerm: string) {
    return node.label?.toLowerCase().includes(searchTerm.toLowerCase()) || node.additionalSearchTerms?.some(a => a?.toLowerCase()?.includes(searchTerm.toLowerCase()));
}

function filterItems(node: TreeViewNode, searchTerm: string) {
    // Search term is blank or this node matches the search criteria...
    if (!searchTerm || nodeMatchesSearchTerm(node, searchTerm)) {
        return node;
    }

    const survivors = _.chain(node.children).map(a => filterItems(a, searchTerm)).filter(a => !!a).value();

    if (survivors?.length > 0) {
        return {
            ...node,
            children: survivors
        };
    } else {
        return null;
    }
}

function takeNLeafNodesFromTree(tree: TreeViewNode[], n: number) {
    let remainder = n;

    const getPrunedMenuItem = (item: TreeViewNode) => {
        if (!item.children?.length) {
            if (remainder > 0) {
                remainder--;
                return item;
            }
            else {
                return null;
            }
        }
        else {
            let trimmedChildren = item.children?.map(getPrunedMenuItem).filter(a => !!a);
            if (!trimmedChildren.length) {
                return null;
            }
            else {
                return { ...item, children: trimmedChildren };
            }
        }
    }

    return tree.map(getPrunedMenuItem).filter(a => !!a);
}

export function getItemIdFromNode(node: TreeViewNode): string | null {
    if (node.value === null || node.value === undefined) {
        return null;
    }

    // catch BigInt and other non-JSON-serializable types
    if (typeof node.value === "bigint") {
        // convert BigInt to string with a prefix
        return `bigint ${node.value.toString()}`;
    } else if (typeof node.value === "function") {
        // convert function to string representation
        return `function ${node.value.toString()}`;
    } else if (typeof node.value === "symbol") {
        // convert symbol to string representation
        return `${node.value.toString()}`;
    } else if (node.value instanceof Date || moment.isMoment(node.value)) {
        // handle Date and Moment.js objects
        return node.value.toISOString();
    } else if (typeof node.value === "string") {
        return node.value;
    }

    return JSON.stringify(node.value);
}

function addIdsToNodes(nodes: TreeViewNode[], getNodeId: (node: TreeViewNode) => string, depth: number = 1): TreeViewNode[] {
    return nodes.map((node) => {
        const nodeId = getNodeId(node);
        return {
            ...node,
            depth,
            id: nodeId,
            children: node.children ? addIdsToNodes(node.children, getNodeId, depth + 1) : []
        };
    });
}

function containsSelectedNodes(selectedNodeValues: string[], currentNode: TreeViewNode): boolean {
    if (!currentNode.children) {
        return false;
    }

    if (currentNode.children.some(c => selectedNodeValues.includes(c.id))) {
        return true;
    }

    return !!currentNode.children.find(node => containsSelectedNodes(selectedNodeValues, node));
}

type NodeTooltipProps = {
    node: TreeViewNode;
    getNodeTooltip: (node: TreeViewNode) => any;
    children: any;
};

function NodeTooltip(props: NodeTooltipProps) {
    const { node, getNodeTooltip, children } = props;
    if (!getNodeTooltip) return <>{children}</>;

    const tooltipContent = getNodeTooltip(node);
    if (!tooltipContent) return <>{children}</>;

    return (
        <Tooltip
            arrow
            placement="left"
            slotProps={{
                popper: {
                    modifiers: [
                        {
                            name: "offset",
                            options: {
                                offset: [0, 24],
                            },
                        },
                    ],
                },
            }}
            title={tooltipContent}
        >
            <div className="col-fill">
                {children}
            </div>
        </Tooltip>
    );
};

const TreeView = React.forwardRef(<T,>(props: TreeViewProps<T>, ref) => {
    const { nodes, slots, multiSelect, multiSelectWithoutCtrl, itemsReordering, maxSelectionCount, defaultSelectedItems, defaultExpandedItems: _defaultExpandedItems, showSearch: _showSearch, defaultSearchTerm, editNodeIcon, acceptEditNodeIcon, cancelEditNodeIcon, defaultExpandIcon, defaultCollapseIcon, dragIcon, onSelect, onLabelChange, onNodeReorder, getNodeSelectionDisabled, getNodeDropAllowed, renderNode: customRenderNode, getNodeTooltip: _getNodeTooltip, getItemId: _getItemId, dataSource: _dataSource, ...rest } = props;
    const [filteredItems, setFilteredItems] = React.useState<TreeViewNode[]>([]);
    const [highlighted, setHighlighted] = React.useState<string[]>([]);
    const [baseNodes, setBaseNodes] = React.useState<TreeViewNode[]>([]);
    const [lazyNodesById, setLazyNodesById] = React.useState<Map<string, TreeViewNode>>(new Map());
    const getItemId = React.useCallback(_getItemId ? _getItemId : getItemIdFromNode, [_getItemId]);
    const allNodesFlat = React.useMemo(
        () => _dataSource ? Array.from(lazyNodesById.values()) : _.flatMap(baseNodes, getFullHierarchy),
        [baseNodes, lazyNodesById, _dataSource]
    );

    // Client-side search requires the full tree up-front and is not supported when children are lazily loaded...
    React.useEffect(() => {
        if (_dataSource && _showSearch) {
            console.warn("TreeView: `showSearch` is not supported when children are lazily loaded via a data source - the search box will not be rendered.");
        }
    }, []);
    const showSearch = _dataSource ? false : _showSearch;

    // Wrap the data source so that every fetched batch is merged into the flat node collection, which drives
    // item rendering, selection, highlighting and tooltips (unknown ids would otherwise render blank items)...
    const dataSource = React.useMemo(() => {
        if (!_dataSource) return undefined;

        return {
            getChildrenCount: _dataSource.getChildrenCount,
            getTreeItems: async (parentId?: string) => {
                const items = await _dataSource.getTreeItems(parentId);

                setLazyNodesById(previous => {
                    const next = new Map(previous);
                    const parentDepth = parentId != null ? (previous.get(parentId)?.depth ?? 0) : 0;
                    items.forEach(item => next.set(item.id, { ...item, depth: parentDepth + 1 }));
                    return next;
                });

                return items;
            }
        };
    }, [_dataSource]);

    const [searchTermSubject$, _setSearchTermSubject$] = React.useState(new BehaviorSubject<string>(""));
    const [searchTerm, setSearchTerm] = React.useState("");
    const [searchTermText, setSearchTermText] = React.useState("");
    const [expandedSearchNodes, setExpandedSearchNodes] = React.useState<string[]>([]);
    const [defaultExpandedItems, setDefaultExpandedItems] = React.useState(_defaultExpandedItems ?? []);

    // track selected nodes
    const [trackSelected, setTrackSelected] = React.useState<TreeViewNode[]>([]);

    React.useEffect(() => {
        const nodesWithIds = addIdsToNodes(nodes ?? [], getItemId);
        if (structuralEqualityExcludingTransientEntries(nodesWithIds, baseNodes)) return;
        setBaseNodes(nodesWithIds);
    }, [nodes, getItemId]);

    React.useEffect(() => {
        if (searchTerm) {
            let filteredResults = _.chain(baseNodes).map(a => filterItems(a, searchTerm)).filter(a => !!a).value();
            filteredResults = takeNLeafNodesFromTree(filteredResults, 200); // Cap the search results at 200 leaf nodes, for performance reasons...
            setFilteredItems(filteredResults);
            setExpandedSearchNodes(_.chain(filteredResults).flatMapDeep(getFullHierarchy).filter(mi => mi?.children?.length > 0).map(mi => mi.id).value());
        }
        else {
            setFilteredItems(baseNodes);
            setExpandedSearchNodes([]);
        }
    }, [baseNodes, searchTerm]);

    React.useEffect(() => {
        const searchTermSubscription = searchTermSubject$
            .pipe(
                tap(setSearchTermText), // Immediately update the text in the input control...
                debounceTime(500)       // And debounce the update of the search term...
            ).subscribe(setSearchTerm);

        return () => searchTermSubscription.unsubscribe();
    }, []);

    React.useEffect(() => {
        if (_.isEqual(searchTerm, defaultSearchTerm)) return;

        searchTermSubject$.next(defaultSearchTerm);
    }, [defaultSearchTerm]);

    const handleNodeSelectionDisabled = React.useCallback((node: TreeViewNode): boolean => {
        if (!node) return false;

        // User has toggled/clicked on a node that prevents selection or the max selection count has been exceeded...
        if (
            (node?.preventSelection || getNodeSelectionDisabled?.(node) || (maxSelectionCount && trackSelected.length >= maxSelectionCount)) &&
            !trackSelected.find(s => s.id === node.id)
        ) {
            return true;
        }

        if (getNodeSelectionDisabled) {
            return getNodeSelectionDisabled(node);
        }
        return false;
    }, [getNodeSelectionDisabled, trackSelected, maxSelectionCount]);

    const handleSelect = React.useCallback((event: React.SyntheticEvent, itemIds: string[] | string) => {
        if (hasAncestorWithClassName(event?.target as HTMLElement, "MuiTreeItem-iconContainer")) return;

        if (multiSelect && multiSelectWithoutCtrl && itemIds.length <= 1) {
            const toggledNodeId = itemIds?.[0];
            if (trackSelected.find(s => s.id === toggledNodeId)) {
                itemIds = trackSelected.filter(s => s.id !== toggledNodeId).map(n => n.id);
            } else {
                itemIds = [...trackSelected.map(n => n.id), toggledNodeId];
            }
        }

        const normalizedNodeIds = _.isArray(itemIds) ? itemIds : [itemIds];
        const selectedNodes = normalizedNodeIds.map(nodeId => allNodesFlat.find(n => _.isEqual(nodeId, n.id))).filter(v => !!v);

        // might be a toggle and thus 
        if (selectedNodes.some(n => handleNodeSelectionDisabled(n))) return;

        setTrackSelected(selectedNodes);
        const selectedNodeValues = selectedNodes.map(n => n.id);
        const highlightedNodes = allNodesFlat.filter(n => selectedNodeValues.includes(n.id) || containsSelectedNodes(selectedNodeValues, n)).map(n => n.id).filter(v => !!v);
        setHighlighted(highlightedNodes);

        if (onSelect) {
            onSelect(selectedNodes);
        }

    }, [onSelect, handleNodeSelectionDisabled, allNodesFlat, trackSelected]);

    const handleLabelChange = React.useCallback((itemId: string, label: string) => {
        const item = allNodesFlat.find(n => _.isEqual(n.id, itemId));
        if (!item) return;

        if (onLabelChange) {
            onLabelChange(item, label);
        }
    }, [onLabelChange, allNodesFlat]);

    const handleNodeReorder = React.useCallback((params: OnNodeReorderParams) => {
        const item = allNodesFlat.find(n => _.isEqual(n.id, params.itemId));
        if (!item) return;

        if (onNodeReorder) {
            onNodeReorder(item, params.oldPosition, params.newPosition);
        }
    }, [onNodeReorder, allNodesFlat]);

    const handleGetNodeDropAllowed = React.useCallback((params: OnNodeReorderParams): boolean => {
        const node = allNodesFlat.find(n => _.isEqual(n.id, params.itemId));
        if (!node) return false;
        if (getNodeDropAllowed) {
            return getNodeDropAllowed(node, params.oldPosition, params.newPosition);
        }
        return true;  // default to true
    }, [getNodeDropAllowed, allNodesFlat]);

    // update highlighted nodes on default selected items
    // also handle default expansion of ancestors of selected nodes
    React.useEffect(() => {
        const selectedNodeValues = defaultSelectedItems ? (Array.isArray(defaultSelectedItems) ? defaultSelectedItems : [defaultSelectedItems]) : [];
        const selectedNodes = selectedNodeValues.map(nodeId => allNodesFlat.find(n => _.isEqual(nodeId, n.id))).filter(v => !!v);
        const highlightedNodes = allNodesFlat.filter(n => selectedNodeValues.includes(n.id) || containsSelectedNodes(selectedNodeValues, n)).map(n => n.id).filter(v => !!v);
        setHighlighted(highlightedNodes);

        const expandedNodes = allNodesFlat.filter(n => containsSelectedNodes(selectedNodeValues, n)).map(n => n.id);
        setDefaultExpandedItems(_.uniq([..._defaultExpandedItems || [], ...expandedNodes]));

        // set track selected
        setTrackSelected(selectedNodes);
    }, [defaultSelectedItems, allNodesFlat]);

    const getNodeTooltip = React.useCallback((node: TreeViewNode) => {
        if (multiSelect && maxSelectionCount && trackSelected?.length >= maxSelectionCount && !trackSelected.find(s => s.id === node.id)) {
            return `A maximum of ${maxSelectionCount} items can be selected.`;
        }

        return _getNodeTooltip?.(node);
    }, [multiSelect, maxSelectionCount, trackSelected, _getNodeTooltip]);

    const renderCustomTreeItem = React.useCallback((
        props: MaterialTreeViewItemProps,
        ref: React.Ref<HTMLLIElement>,
    ) => {
        const { id, itemId, label, disabled, children, ...other } = props;
        const item = allNodesFlat.find(n => _.isEqual(n.id, itemId));
        if (!item) return null;

        const {
            getContextProviderProps,
            getRootProps,
            getContentProps,
            getIconContainerProps,
            getCheckboxProps,
            getLabelProps,
            getGroupTransitionProps,
            getDragAndDropOverlayProps,
            getLabelInputProps,
        } = useTreeItem({ id, itemId, children, label, disabled, rootRef: ref });
        const { interactions, status } = useTreeItemUtils({
            itemId: itemId,
            children: children,
        });

        const { draggable, onDragStart, ...otherRootProps } = getRootProps(other);
        const { value, ...otherLabelInputProps } = getLabelInputProps();

        const handleDragStart = (event: React.DragEvent) => {
            if (!onDragStart) {
                return;
            }

            onDragStart(event);
            event.dataTransfer.setDragImage(
                (event.target as HTMLElement).parentElement!,
                0,
                0,
            );
        };

        return (
            <TreeItemProvider {...getContextProviderProps()}>
                <TreeItemRoot {...otherRootProps} className={clsx("MuiTreeItem-root", highlighted.includes(item?.id) && "active", (handleNodeSelectionDisabled?.(item)) && "not-selectable")}>
                    <TreeItemContent {...getContentProps()} className="MuiTreeItem-content">
                        <TreeItemIconContainer {...getIconContainerProps()} className="MuiTreeItem-iconContainer">
                            <TreeItemIcon status={status} />
                        </TreeItemIconContainer>
                        {
                            // add a drag handle to prevent the entire item from being draggable
                            itemsReordering &&
                            <TreeItemIconContainer draggable={draggable} onDragStart={handleDragStart} className=".treeview-drag-icon">
                                {dragIcon ? dragIcon : <DragVertical size="small" color="gray" />}
                            </TreeItemIconContainer>
                        }
                        <TreeItemCheckbox {...getCheckboxProps()} className="MuiTreeItem-checkbox" />
                        {
                            !status.editing ?
                                <NodeTooltip getNodeTooltip={getNodeTooltip} node={item}>
                                    <TreeItemLabel {...getLabelProps()} className="MuiTreeItem-label">
                                        {
                                            customRenderNode ? customRenderNode(item, item?.depth, searchTerm) :
                                                searchTerm ? <SearchTermHighlight text={item?.label} searchTerm={searchTerm} /> :
                                                    item?.label
                                        }
                                    </TreeItemLabel>
                                </NodeTooltip> :
                                <TreeItemLabelInput {...otherLabelInputProps} value={value} />
                        }
                        {
                            status.editable && (
                                !status.editing ?
                                    <IconButton
                                        onClick={interactions.toggleItemEditing}
                                    >
                                        {
                                            editNodeIcon ?
                                                editNodeIcon :
                                                <Pencil size="small" color="gray" />
                                        }
                                    </IconButton> :
                                    <div className="d-flex gap-1">
                                        <IconButton
                                            onClick={(event: React.MouseEvent) => interactions.handleSaveItemLabel(event, value)}
                                        >
                                            {
                                                acceptEditNodeIcon ?
                                                    acceptEditNodeIcon :
                                                    <Check size="small" color="gray" />
                                            }
                                        </IconButton>
                                        <IconButton
                                            onClick={interactions.handleCancelItemLabelEditing}
                                        >
                                            {
                                                cancelEditNodeIcon ?
                                                    cancelEditNodeIcon :
                                                    <X size="small" color="gray" />
                                            }
                                        </IconButton>
                                    </div>
                            )
                        }
                        <TreeItemDragAndDropOverlay {...getDragAndDropOverlayProps()} />
                    </TreeItemContent>
                    {children && <TreeItemGroupTransition {...getGroupTransitionProps()} className="MuiTreeItem-groupTransition" />}
                </TreeItemRoot>
            </TreeItemProvider>
        );
    }, [
        itemsReordering,
        highlighted,
        allNodesFlat,
        searchTerm,
        editNodeIcon,
        acceptEditNodeIcon,
        cancelEditNodeIcon,
        dragIcon,
        handleNodeSelectionDisabled
    ]);

    const CustomTreeItem = React.forwardRef(renderCustomTreeItem);

    return (
        <div className="treeview-component-container" {...getDataProps(props)}>
            {
                showSearch &&
                <div className="search-row">
                    <OutlinedInput
                        size="small"
                        placeholder="Search"
                        className="search-menu-items-input"
                        fullWidth
                        startAdornment={
                            <InputAdornment position="start">
                                <Search size="small" />
                            </InputAdornment>
                        }
                        value={searchTermText}
                        onChange={(event) => searchTermSubject$.next(event.target.value)}
                    />
                </div>
            }
            {
                (!!dataSource || filteredItems?.length > 0) ?
                    <MaterialTreeView
                        ref={ref}
                        items={dataSource ? [] : filteredItems}
                        {...(dataSource ? { dataSource } : {})}
                        onSelectedItemsChange={handleSelect}
                        onItemLabelChange={handleLabelChange}
                        onItemPositionChange={handleNodeReorder}
                        canMoveItemToNewPosition={handleGetNodeDropAllowed}
                        expansionTrigger="iconContainer"
                        expandedItems={searchTerm ? expandedSearchNodes : undefined}
                        defaultSelectedItems={defaultSelectedItems}
                        defaultExpandedItems={defaultExpandedItems}
                        multiSelect={multiSelect ? true : null}
                        slots={{
                            expandIcon: (params) => defaultExpandIcon ?? <TriangleRight {...params} size="medium" />,
                            collapseIcon: (params) => defaultCollapseIcon ?? <TriangleDown {...params} size="medium" />,
                            item: CustomTreeItem,
                            ...slots,
                        }}
                        itemsReordering={itemsReordering}
                        {...(removeDataProps(rest) as any)}
                    /> :
                    <div className="no-items">No options available.</div>
            }

        </div>
    )
});

export { TreeView };